import { MutationTree } from 'vuex';
import { AuthState, User } from './types';
import * as types from './mutation-types';
import { AppContext } from '../../context';

export const mutations: MutationTree<AuthState> = {
    [types.PROGRESS_START](state) {
        // エラークリア
        state.errorCode = undefined;
        state.errorMsg = undefined;
        state.embedded.errorCode = undefined;
        state.embedded.errorMsg = undefined;
        state.error = false;
        state.embedded.error = false;
        // 通知クリア
        state.embedded.notice = undefined;
        state.notice = undefined;
        // 処理開始
        state.processing = true;
    },

    [types.PROGRESS_END](state) {
        state.processing = false;
    },

    [types.LOGIN_SUCCESS](state, payload: User) {
        localStorage.setItem(
            AppContext.InternalConfig.LocalStorage.AUTH_KEY,
            JSON.stringify({
                id: payload.id,
                uid: payload.uid,
                name: payload.name,
                afterLogin: true,
            })
        );
        state.myAuthorities.list = payload.authorities;
        state.error = false;
        state.processing = false;
        state.isUpdated = true;
    },

    [types.SSO_SUCCESS](state) {
        state.error = false;
        state.processing = false;
        state.isUpdated = true;
    },

    [types.LOGOUT_SUCCESS](state) {
        localStorage.removeItem(AppContext.InternalConfig.LocalStorage.AUTH_KEY);
        state.error = false;
        state.processing = false;
    },

    [types.OVERWRITE_USER](state, payload: { name: string }) {
        const authData = JSON.parse(localStorage.getItem(AppContext.InternalConfig.LocalStorage.AUTH_KEY));
        localStorage.setItem(
            AppContext.InternalConfig.LocalStorage.AUTH_KEY,
            JSON.stringify({
                id: authData.id,
                uid: authData.uid,
                name: payload.name,
            })
        );
        state.isUpdated = true;
    },

    [types.PROGRESS_FAILURE](state, payload: { code: string; message: string }) {
        state.errorCode = payload.code;
        state.errorMsg = payload.message;
        state.embedded.errorCode = undefined;
        state.embedded.errorMsg = undefined;
        state.error = true;
        state.embedded.error = false;
        state.processing = false;
    },

    [types.DEAL_ERROR](state) {
        state.errorCode = undefined;
        state.errorMsg = undefined;
        state.embedded.errorCode = undefined;
        state.embedded.errorMsg = undefined;
        state.error = false;
        state.embedded.error = false;
    },

    [types.DEAL_UPDATE](state) {
        state.isUpdated = false;
    },

    [types.DEAL_NOTICE](state) {
        state.embedded.notice = undefined;
        state.notice = undefined;
    },

    [types.AUTHORIZED_ERROR](state) {
        localStorage.removeItem(AppContext.InternalConfig.LocalStorage.AUTH_KEY);
        state.processing = false;
    },

    [types.UNEXPECTED_ERROR](state) {
        state.errorCode = AppContext.Error.Unexpected.CODE;
        state.errorMsg = AppContext.Error.Unexpected.MESSAGE_KEY;
        state.error = true;
        state.processing = false;
    },

    [types.AUTHORITIES_START](state) {
        const myAuthoritiesState = state.myAuthorities;
        myAuthoritiesState.isProcessing = true;
    },

    [types.AUTHORITIES_FAILURE](state, payload: { code: string; message: string }) {
        const myAuthoritiesState = state.myAuthorities;
        myAuthoritiesState.errorCode = payload.code;
        myAuthoritiesState.errorMsg = payload.message;
        myAuthoritiesState.error = true;
        myAuthoritiesState.isProcessing = false;
    },

    [types.AUTHORITIES_SUCCESS](state, payload: { authorities: Array<string> }) {
        const myAuthoritiesState = state.myAuthorities;
        myAuthoritiesState.list = payload.authorities;
        myAuthoritiesState.isProcessing = false;
    },

    [types.CLEAR_AUTH_DATA]() {
        localStorage.removeItem(AppContext.InternalConfig.LocalStorage.AUTH_KEY);
    },

    [types.OVERWRITE_AUTH_DATA](state, payload: User) {
        localStorage.setItem(
            AppContext.InternalConfig.LocalStorage.AUTH_KEY,
            JSON.stringify({
                id: payload.id,
                uid: payload.uid,
                name: payload.name,
            })
        );
    },

    [types.REGISTER_SUCCESS](state, payload: { message: string }) {
        state.notice = payload.message;
        state.processing = false;
    },

    [types.SOFT_REGISTER_RESET](state) {
        const softRegistered = state.softRegistered;
        softRegistered.error = false;
    },

    [types.SOFT_REGISTER_START](state) {
        const softRegistered = state.softRegistered;
        softRegistered.isProcessing = true;
    },

    [types.SOFT_REGISTER_SUCCESS](state, payload: { message: string; secret_key: string }) {
        const softRegistered = state.softRegistered;
        softRegistered.secret_key = payload.secret_key;
        state.notice = payload.message;
        state.processing = false;
        softRegistered.isProcessing = false;
    },

    [types.SOFT_REGISTER_FAILURE](state) {
        const softRegistered = state.softRegistered;
        softRegistered.error = true;
        softRegistered.isProcessing = false;
    },

    [types.SEND_MAIL_START](state) {
        const sendMail = state.sendMail;
        sendMail.isProcessing = true;
    },

    [types.SEND_MAIL_SUCCESS](state, payload: { message: string }) {
        const sendMail = state.sendMail;
        state.notice = payload.message;
        state.processing = false;
        sendMail.isProcessing = false;
    },

    [types.SEND_MAIL_FAILURE](state) {
        const sendMail = state.sendMail;
        sendMail.isProcessing = false;
    },

    [types.BEFORE_PASSWORD_CHANGE](state, payload: User) {
        localStorage.setItem(
            AppContext.InternalConfig.LocalStorage.AUTH_KEY,
            JSON.stringify({
                id: payload.id,
            })
        );
    },

    [types.UPDATE_FORGOTTEN_PASSWORD_START](state) {
        const updateForgottenPassword = state.updateForgottenPassword;
        updateForgottenPassword.isProcessing = true;
    },

    [types.UPDATE_FORGOTTEN_PASSWORD_SUCCESS](state, payload: { message: string }) {
        const updateForgottenPassword = state.updateForgottenPassword;
        state.notice = payload.message;
        state.processing = false;
        updateForgottenPassword.isProcessing = false;
    },

    [types.UPDATE_FORGOTTEN_PASSWORD_FAILURE](state) {
        const updateForgottenPassword = state.updateForgottenPassword;
        updateForgottenPassword.isProcessing = false;
    },

    // FATALエラー系
    [types.FATAL_FAILURE](state) {
        const myAuthoritiesState = state.myAuthorities;
        myAuthoritiesState.isProcessing = false;
    },

    // システムメッセージを表示させるフラグ
    [types.AFTER_LOGIN_CLEAR](state) {
        const authData = JSON.parse(localStorage.getItem(AppContext.InternalConfig.LocalStorage.AUTH_KEY));
        localStorage.setItem(
            AppContext.InternalConfig.LocalStorage.AUTH_KEY,
            JSON.stringify({
                id: authData.id,
                uid: authData.uid,
                name: authData.name,
                afterLogin: false,
            })
        );
    },
};
